import React, { useCallback, useEffect, useState } from "react";
import { Box, Center, Module, util } from "@thecb/components";
import { isCBAdminRole, isClientAdminRole } from "@/util/common";
import {
  AGENCY_ADMIN,
  CLIENT_ADMIN,
  FEDERATION,
  OKTA_CLIENT_ADMIN,
  NO_ROLE,
  SELECT_ALL_SLUG,
} from "@/sanctuary/typedefs";
import InternalUserInfoForm from "@/components/administeredUsers/InternalUserInfoForm";
import AlertBar from "@/components/alert-bar";
import {
  ADD,
  CREATE_CLIENT_ADMIN,
  EDIT,
  PERSONAL_INFORMATION,
  PROFILE,
} from "@/constants/formTypes";
import { userWithRoleCanEditNames } from "../../util/common";
import { useOktaAuth } from "@okta/okta-react";
import { OKTA } from "../../sanctuary/typedefs";

const InternalUserInfo = ({
  activateUser,
  alertBar,
  alertBarActions,
  allAgencyOptions,
  allSelected,
  clientOptions,
  closeForm,
  currentUser,
  currentUserState,
  deactivateUser,
  fetchAgencyOptions,
  form,
  formActions,
  formType,
  isPOSEnabled,
  modificationStatuses,
  openChangePasswordForm,
  roleCombinations,
  roleOptions,
  selectAgency,
  selectedAgencies,
  setSelectedAgencies,
  submitType,
  toggleSelectAllAgencies,
  userBeingAdded,
  userBeingEdited,
  userIsBeingChecked,
  userCanActivateUser,
  userCanDeactivateUser,
}) => {
  const safeRole = form?.fields?.role?.rawValue;
  const safePosRole = form?.fields?.pos_role?.rawValue;
  const [errors, handleErrors] = useState(
    form?.fields?.pos_role?.hasErrors ?? false
  );
  const [disableSelection, setDisableSelection] = useState(false);
  const [disableRole, setDisableRole] = useState(false);
  const [configuredRoleOptions, setRoleOptions] = useState(roleOptions);
  const [disableNames, setDisableNames] = useState(false);
  const [disableEmail, setDisableEmail] = useState(false);
  const safeCurrentUserRole = currentUser?.role?.value?.value;
  const safeUserBeingEditedRole = userBeingEdited?.value?.role?.value?.value;
  const safeUserBeingEditedIDP = userBeingEdited?.value?.credentialsProvider?.value;
  const safeCurrentUserAgencies = currentUser?.agencies?.value;
  const safeUserBeingEditedAgencies = userBeingEdited?.value?.agencies?.value;
  const isActive = userBeingEdited?.value?.isActive?.value ?? false;
  const userStatus = userBeingEdited?.value?.status?.value;

  const shouldDisableRoleSelection = useCallback(
    () =>
      formType === CREATE_CLIENT_ADMIN ||
      (formType === EDIT && !isActive) ||
      (safeCurrentUserRole === AGENCY_ADMIN &&
        safeUserBeingEditedRole === CLIENT_ADMIN) ||
      isCBAdminRole(safeUserBeingEditedRole),
    [formType, isActive, safeCurrentUserRole, safeUserBeingEditedRole]
  );

  const shouldDisableEmail = useCallback(
    () =>
      formType === EDIT ||
      formType === PROFILE ||
      (formType === ADD && userBeingAdded) ||
      safeUserBeingEditedIDP === FEDERATION,
    [formType, safeUserBeingEditedIDP]
  );

  const findMatchingAgencies = useCallback(
    () =>
      safeUserBeingEditedAgencies?.reduce(
        (acc, curr) =>
          Array.isArray(safeCurrentUserAgencies) && safeCurrentUserAgencies.length
            ? safeCurrentUserAgencies.find((item) => {
                const currentUserSlug = item?.slug ?? "";
                const slug = curr?.slug ?? "";

                return currentUserSlug === slug;
              })
              ? [...acc, curr.slug]
              : acc
            : acc,
        []
      ),
    [safeCurrentUserAgencies, safeUserBeingEditedAgencies]
  );
  const safeMatchedUserAgencies = formType === EDIT ? findMatchingAgencies() : [];

  useEffect(() => {
    if (allAgencyOptions && allAgencyOptions.length < 1) {
      fetchAgencyOptions(currentUser?.agencies?.value);
    }
  }, [allAgencyOptions, currentUser?.agencies?.value, fetchAgencyOptions]);

  useEffect(() => {
    if (userBeingEdited?.value && formType === EDIT) {
      const safeUserAgencies = userBeingEdited?.value?.agencies?.value;
      const safeFirstName = userBeingEdited?.value?.firstName?.value;
      const safeLastName = userBeingEdited?.value?.lastName?.value;
      const safeEmail = userBeingEdited?.value?.email?.value;
      const safeRole = userBeingEdited?.value?.role?.value?.value;
      const matchedUserAgencies = findMatchingAgencies();
      formActions.actions.fields.firstName.set(safeFirstName);
      formActions.actions.fields.lastName.set(safeLastName);
      formActions.actions.fields.email.set(safeEmail);
      formActions.actions.fields.role.set(safeRole);
      setSelectedAgencies(safeUserAgencies);
      setDisableRole(shouldDisableRoleSelection());
      setDisableEmail(shouldDisableEmail());
      setDisableNames(
        !isActive ||
          !userWithRoleCanEditNames(safeCurrentUserRole, matchedUserAgencies)
      );
      if (
        isCBAdminRole(safeRole) ||
        safeRole === OKTA_CLIENT_ADMIN ||
        safeRole === CLIENT_ADMIN
      ) {
        setDisableSelection(true);
        toggleSelectAllAgencies(true);
        setRoleOptions(roleOptions);
      } else if (
        safeUserAgencies?.some((agency) => agency?.slug === SELECT_ALL_SLUG)
      ) {
        // User has the "all" agency marker — toggle Select All to reflect
        // their stored state and keep the checkbox in sync.
        toggleSelectAllAgencies(true);
      }
    }
    if (userBeingEdited && formType === PROFILE) {
      const safeFirstName = userBeingEdited?.firstName?.value;
      const safeLastName = userBeingEdited?.lastName?.value;
      const safeEmail = userBeingEdited?.email?.value;
      const credentialsProvider = userBeingEdited?.credentialsProvider?.value;
      setDisableEmail(shouldDisableEmail());
      setDisableNames(credentialsProvider !== OKTA);
      formActions.actions.fields.firstName.set(safeFirstName);
      formActions.actions.fields.lastName.set(safeLastName);
      formActions.actions.fields.email.set(safeEmail);
    }
    if (userBeingAdded && formType === ADD) {
      const safeFirstName = userBeingAdded?.firstName?.value;
      const safeLastName = userBeingAdded?.lastName?.value;
      const safeEmail = userBeingAdded?.email?.value;
      formActions.actions.fields.firstName.set(safeFirstName);
      formActions.actions.fields.lastName.set(safeLastName);
      formActions.actions.fields.email.set(safeEmail);
      setSelectedAgencies([]);
      setDisableEmail(shouldDisableEmail());
    }
  }, [
    findMatchingAgencies,
    formActions.actions.fields.email,
    formActions.actions.fields.firstName,
    formActions.actions.fields.lastName,
    formActions.actions.fields.role,
    formType,
    isActive,
    safeCurrentUserRole,
    safeUserBeingEditedIDP,
    setSelectedAgencies,
    shouldDisableEmail,
    shouldDisableRoleSelection,
    toggleSelectAllAgencies,
    userBeingEdited,
    roleOptions,
  ]);

  useEffect(() => {
    if (formType === CREATE_CLIENT_ADMIN) {
      formActions.actions.fields.role.set(CLIENT_ADMIN);
      setDisableRole(shouldDisableRoleSelection());
    }
    if (formType === ADD) {
      formActions.actions.fields.role.set(NO_ROLE);
      if (isPOSEnabled) formActions.actions.fields.pos_role.set(NO_ROLE);
    }
  }, [formActions.actions.fields.role, formType, shouldDisableRoleSelection]);

  useEffect(() => {
    if (formType === EDIT || formType === ADD) {
      if (isClientAdminRole(safeRole) || isClientAdminRole(safePosRole)) {
        setDisableSelection(true);
        toggleSelectAllAgencies(true);
      } else if (isCBAdminRole(safePosRole) || isCBAdminRole(safeRole)) {
        setDisableSelection(true);
      } else {
        setDisableSelection(false);
      }
    }
  }, [
    safeRole,
    safePosRole,
    formType,
    setDisableSelection,
    toggleSelectAllAgencies,
  ]);

  const { oktaAuth } = useOktaAuth();

  const handleSubmit = util.general.generateClickHandler(
    form,
    () => handleErrors(true),
    () => submitType({ oktaAuth })
  );

  return (
    <Center maxWidth="496px">
      <Box padding="0" width="100%">
        <Box padding="0 0 16px">
          {alertBar.alerts.length > 0 && (
            <Box padding="1rem 0 0">
              <AlertBar {...alertBar} {...alertBarActions} />
            </Box>
          )}
          <Module
            heading={
              formType === PERSONAL_INFORMATION
                ? "Add a New User"
                : formType === PROFILE
                  ? "Profile"
                  : formType === CREATE_CLIENT_ADMIN
                    ? "Create Client Admin"
                    : formType === ADD
                      ? "Add User"
                      : "Edit User"
            }
          >
            <InternalUserInfoForm
              {...form}
              {...formActions}
              currentUserState={currentUserState}
              activateUser={activateUser}
              allAgencyOptions={allAgencyOptions}
              allSelected={allSelected}
              clearOnDismount
              clientOptions={clientOptions}
              closeForm={closeForm}
              currentUserRole={safeCurrentUserRole}
              deactivateUser={deactivateUser}
              disableRole={disableRole}
              emailDisabled={disableEmail}
              firstName={userBeingEdited?.value?.firstName?.value ?? ""}
              form={form}
              formType={formType}
              handleErrors={handleErrors}
              isActive={isActive}
              isPOSEnabled={isPOSEnabled}
              lastName={userBeingEdited?.value?.lastName?.value ?? ""}
              matchedUserAgencies={safeMatchedUserAgencies}
              modificationStatuses={modificationStatuses}
              namesDisabled={disableNames}
              openChangePasswordForm={openChangePasswordForm}
              roleOptions={configuredRoleOptions}
              selectAgency={selectAgency}
              selectedAgencies={selectedAgencies}
              selectionDisabled={disableSelection}
              showErrors={errors}
              submitType={submitType}
              toggleSelectAllAgencies={toggleSelectAllAgencies}
              userBeingEdited={userBeingEdited}
              userBeingEditedRole={safeUserBeingEditedRole}
              userCanActivateUser={userCanActivateUser}
              userCanDeactivateUser={userCanDeactivateUser}
              userId={userBeingEdited?.value?.id?.value ?? ""}
              userIsBeingChecked={userIsBeingChecked}
              userStatus={userStatus}
              roleCombinations={roleCombinations}
              setDisableSelection={setDisableSelection}
              handleSubmit={handleSubmit}
            />
          </Module>
        </Box>
      </Box>
    </Center>
  );
};

export default InternalUserInfo;
